<template>
  <div>
    <ui-stage v-if="data">
      <ui-tabs v-model="tab" type="border-card">
        <ui-tab-pane name="main" label="编辑">
          <ui-admin-form
            :compact="compact"
            :readonly="readonly"
            :fields="attributes"
            :model="data"
            :module-url="moduleUrl"
            with-delete
            @submit="handleSubmit"
          >
            <template v-slot:field__allowedProjects="{ value, onInput }">
              <div class="project-selector">
                <h4>选择允许查看的项目</h4>
                <div class="project-list">
                  <div class="project-item" v-for="project in projects" :key="project.id">
                    <ui-checkbox
                      :value="isProjectSelected(project.id, value)"
                      @input="toggleProject(project.id, value, onInput)"
                    >
                      {{ project.title }}
                    </ui-checkbox>
                  </div>
                </div>
                <div class="selected-projects" v-if="getSelectedProjects(value).length">
                  <h5>已选择的项目：</h5>
                  <div class="selected-list">
                    <span
                      class="selected-item"
                      v-for="projectId in getSelectedProjects(value)"
                      :key="projectId"
                    >
                      {{ getProjectName(projectId) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
            <template v-slot:field__password>
              <div class="password-field" v-if="isEdit">
                <div class="password-info">
                  <p>密码已加密存储，无法查看原始密码</p>
                </div>
                <ui-button type="primary" @click="resetPassword"> 重置并复制密码 </ui-button>
              </div>
              <div class="password-field" v-else>保存后在此点击按钮复制密码</div>
            </template>
          </ui-admin-form>
        </ui-tab-pane>
      </ui-tabs>
    </ui-stage>
  </div>
</template>

<script>
import { adminEdit } from '@yishitec/web/mixins';

export default {
  name: 'Edit',
  mixins: [adminEdit],
  data() {
    return {
      withCustomFields: false,
      projects: [],
    };
  },
  computed: {
    isEdit() {
      return this.$route.params.vId;
    },
  },
  async mounted() {
    await this.loadProjects();
  },
  methods: {
    async loadProjects() {
      try {
        const res = await this.api('/api/projects/list', {
          state: 'ACTIVE',
        })();
        this.projects = res.list || [];
      } catch (e) {
        console.error('加载项目列表失败', e);
      }
    },

    isProjectSelected(projectId, allowedProjects) {
      if (!allowedProjects) return false;

      try {
        const projectIds = JSON.parse(allowedProjects);
        return Array.isArray(projectIds) && projectIds.includes(projectId);
      } catch (e) {
        return false;
      }
    },

    toggleProject(projectId, allowedProjects, onInput) {
      try {
        let projectIds = [];
        if (allowedProjects) {
          try {
            projectIds = JSON.parse(allowedProjects);
            if (!Array.isArray(projectIds)) projectIds = [];
          } catch (parseError) {
            console.warn('JSON解析失败，重置为空数组', parseError);
            projectIds = [];
          }
        }

        const index = projectIds.indexOf(projectId);
        if (index > -1) {
          projectIds.splice(index, 1);
        } else {
          projectIds.push(projectId);
        }

        onInput(JSON.stringify(projectIds));
      } catch (e) {
        console.error('切换项目选择失败', e);
        // 如果出现其他错误，重置为空数组
        onInput(JSON.stringify([]));
      }
    },

    getSelectedProjects(allowedProjects) {
      if (!allowedProjects) return [];

      try {
        const projectIds = JSON.parse(allowedProjects);
        return Array.isArray(projectIds) ? projectIds : [];
      } catch (e) {
        return [];
      }
    },

    getProjectName(projectId) {
      const project = this.projects.find((p) => p.id == projectId);
      return project ? project.title : `项目${projectId}`;
    },

    async resetPassword() {
      try {
        // 生成随机密码（8位字母数字组合）
        const newPassword = this.generateRandomPassword();

        // 调用重置密码API
        await this.api(`/api/monitor-viewers/reset-password/${this.$route.params.vId}`, {
          newPassword,
        })();

        // 复制账号密码到剪贴板
        const accountInfo = `用户名：${this.data.username}\n密码：${newPassword}`;
        await this.copyToClipboard(accountInfo);

        this.$message.success('密码重置成功，账号信息已复制到剪贴板');
      } catch (e) {
        console.error('重置密码失败', e);
        this.$message.error('重置密码失败：' + (e.message || '未知错误'));
      }
    },

    generateRandomPassword() {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let password = '';
      for (let i = 0; i < 8; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password;
    },

    async copyToClipboard(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          // 降级方案
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
      } catch (e) {
        console.error('复制到剪贴板失败', e);
        throw new Error('复制到剪贴板失败');
      }
    },
  },
};
</script>

<style lang="less" scoped>
.password-field {
  .password-info {
    margin-bottom: 10px;

    p {
      margin: 0;
      color: #909399;
      font-size: 14px;
    }
  }
}

.project-selector {
  line-height: 1.8;

  .project-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 10px;

    .project-item {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .selected-projects {
    margin-top: 15px;

    h5 {
      margin: 0 0 10px 0;
      color: #606266;
      font-size: 14px;
    }

    .selected-list {
      .selected-item {
        display: inline-block;
        background: #f0f9ff;
        color: #1890ff;
        padding: 4px 8px;
        margin: 2px 4px 2px 0;
        border-radius: 4px;
        font-size: 12px;
      }
    }
  }
}
</style>
